import React from 'react';
import { LineChart, BarChart3, ArrowUpRight, CheckCircle2 } from 'lucide-react';

const features = [
  'Subscriber growth tracking with weekly cohort views',
  'Open + click + reply attribution to revenue',
  'Subject-line A/B engine with auto-winner rollout',
  'Deliverability dashboard (Google Postmaster + Mailgun)',
  'Funnel revenue per email — not just per campaign',
];

const ManageGrow = () => {
  return (
    <section className="container-x section" data-testid="manage-grow">
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <p className="text-xs uppercase tracking-[0.25em] text-[var(--gh-orange)] font-semibold">
            Reporting & ops
          </p>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold mt-3 leading-[1.05] text-ink">
            Manage and grow with{' '}
            <span className="font-serif italic text-[var(--gh-orange)]">data you trust</span>.
          </h2>
          <p className="mt-5 text-base md:text-lg text-muted-warm leading-relaxed">
            Most email reports are vanity. Ours tie every campaign to a number that pays
            the bills — opens you can stake a strategy on, clicks that map to revenue, and
            deliverability you can actually fix.
          </p>

          <ul className="mt-8 space-y-3">
            {features.map((f) => (
              <li key={f} className="flex items-start gap-3 text-[15px] text-ink">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-[var(--gh-orange-soft)] text-[var(--gh-orange)] flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-3 h-3" />
                </span>
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <a
            href="/case-studies"
            data-testid="manage-grow-cta"
            className="btn-orange mt-10"
          >
            See sample dashboards <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>

        {/* Right: illustrative chart "card" */}
        <div className="lg:col-span-6">
          <div className="relative rounded-[1.75rem] bg-cream-2 border border-line p-6 md:p-8 shadow-[0_30px_60px_-30px_rgba(10,10,10,0.18)]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-warm">Statistics</p>
                <p className="font-display text-2xl font-bold text-ink">Sales analytics</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-cream border border-line">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--gh-orange)]" />
                  Last year
                </span>
                <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-ink text-[var(--gh-cream)]">
                  <span className="w-1.5 h-1.5 rounded-full bg-white" />
                  This year
                </span>
              </div>
            </div>

            {/* Donut + bars */}
            <div className="mt-6 grid grid-cols-5 gap-5 items-center">
              <div className="col-span-2 flex items-center justify-center">
                <div className="relative w-36 h-36">
                  <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                    <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#e8e1d3" strokeWidth="3.2" />
                    <circle
                      cx="18" cy="18" r="15.9155"
                      fill="none"
                      stroke="var(--gh-orange)"
                      strokeWidth="3.2"
                      strokeDasharray="68 100"
                      strokeLinecap="round"
                    />
                    <circle
                      cx="18" cy="18" r="15.9155"
                      fill="none"
                      stroke="var(--gh-ink)"
                      strokeWidth="3.2"
                      strokeDasharray="22 100"
                      strokeDashoffset="-68"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-[10px] uppercase tracking-widest text-muted-warm">Email LTV</span>
                    <span className="font-display text-2xl font-extrabold text-ink">+212%</span>
                  </div>
                </div>
              </div>
              <div className="col-span-3">
                <div className="flex items-end gap-2 h-32">
                  {[28, 36, 22, 48, 38, 60, 72, 58, 84, 70, 92, 80].map((h, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                      <div
                        className="w-full rounded-t-md transition-all"
                        style={{
                          height: `${h}%`,
                          background: i % 3 === 0 ? 'var(--gh-orange)' : '#1f1d1a',
                          opacity: i % 3 === 0 ? 1 : 0.85,
                        }}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-2 text-[10px] uppercase tracking-widest text-muted-warm">
                  <span>Jan</span><span>Apr</span><span>Jul</span><span>Oct</span><span>Dec</span>
                </div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3">
              {[
                { Icon: LineChart, label: 'Open rate', value: '54.2%' },
                { Icon: BarChart3, label: 'CTR', value: '8.6%' },
                { Icon: LineChart, label: 'Revenue / email', value: '$1.42' },
              ].map((m) => (
                <div key={m.label} className="rounded-2xl bg-cream border border-line p-4">
                  <m.Icon className="w-4 h-4 text-[var(--gh-orange)]" />
                  <p className="mt-2 text-[10px] uppercase tracking-widest text-muted-warm">{m.label}</p>
                  <p className="font-display text-xl font-bold text-ink">{m.value}</p>
                </div>
              ))}
            </div>

            <div className="absolute -top-5 -right-5 hidden md:block float-card px-4 py-3 animate-float-y">
              <p className="text-[10px] uppercase tracking-widest text-muted-warm">Latest</p>
              <p className="text-sm font-bold text-ink">Birthday promo · 4× sales</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ManageGrow;
